import React, {
  Suspense,
  useEffect,
  useState,
  useCallback,
  useMemo,
  memo,
} from "react";
import { Box, useMediaQuery, ThemeProvider } from "@mui/material";
import PropTypes from "prop-types";
import SuspanceLoading from "../components/SuspanceLoading";
import Header from "../components/Homepage/Header";
import { useLocation } from "react-router-dom";
import Navbar from "../components/LawyerCompo/Dashboard/Navbar";
import Footer from "../components/Homepage/Footer";
import { useSubscription } from "../components/Auth/SubscriptionContext";
import { DialogProvider } from "../components/DialogContext";
import SubscriptionStatusPage from "../components/Auth/SubscriptionStatusPage";
import SubscriptionAlert from "../components/Auth/SubscriptionAlert"; // Add this import
import SideNavBar from "../components/LawyerCompo/Dashboard/SideNavBar";
import DashboardLayout from "./DashboardLayout";
import { landingPaths } from "./Routes";
import { baseTheme } from "../ThemeUtils/baseTheme";

const StickyHeader = memo(({ isVisible, xs }) => (
  <Box
    sx={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      p: "0.3rem",
      backdropFilter: "blur(10px)",
      transform: isVisible ? "translateY(0)" : "translateY(-100%)",
      transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      willChange: "transform",
    }}
  >
    <Header />
  </Box>
));
StickyHeader.displayName = "StickyHeader";

const LoadingOverlay = memo(() => (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(255,255,255,0.75)",
      zIndex: 10,
    }}
  >
    <SuspanceLoading />
  </Box>
));
LoadingOverlay.displayName = "LoadingOverlay";

const SubscriptionOverlay = memo(({ xs }) => (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10,
      p: xs ? "0.5rem" : "1rem",
      backgroundColor: "rgba(240, 242, 245, 0.85)",
    }}
  >
    <Box
      sx={{
        width: "100%",
        maxWidth: "fit-content",
        maxHeight: "80vh",
        overflowY: "auto",
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 3,
        p: 2,
      }}
    >
      <SubscriptionStatusPage lockScreen={true} />
    </Box>
  </Box>
));
SubscriptionOverlay.displayName = "SubscriptionOverlay";

const Layout = ({ children }) => {
  const location = useLocation();
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const { hasActivePlan, ready, subscription } = useSubscription();
  const xs = useMediaQuery("(max-width: 600px)");
  const urlPath = location.pathname;

  // Memoize path checks
  const isLandingPath = useMemo(() => {
    return landingPaths.some(
      (path) => urlPath === path || urlPath.startsWith(`${path}/`),
    );
  }, [urlPath]);

  const isClientPath = useMemo(() => urlPath.startsWith("/client"), [urlPath]);
  const isDashboardPath = useMemo(
    () => urlPath.startsWith("/dashboard"),
    [urlPath],
  );

  // Optimized scroll handler for landing pages
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const scrollThreshold = 10;

    if (Math.abs(currentScrollY - lastScrollY) < scrollThreshold) return;

    const shouldHide = currentScrollY > lastScrollY && currentScrollY > 100;
    const shouldShow = currentScrollY < lastScrollY;

    if (shouldHide) {
      setIsHeaderVisible(false);
    } else if (shouldShow) {
      setIsHeaderVisible(true);
    }

    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  // Attach scroll listener only for landing pages
  useEffect(() => {
    if (!isLandingPath) return;

    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        handleScroll();
        rafId = null;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isLandingPath, handleScroll]);

  // Reset header visibility on route change
  useEffect(() => {
    setIsHeaderVisible(true);
    setLastScrollY(0);
  }, [urlPath]);

  // Memoize the showSubscriptionOverlay check
  const showSubscriptionOverlay = useMemo(() => {
    return ready && !hasActivePlan() && isDashboardPath;
  }, [ready, hasActivePlan, isDashboardPath]);

  // Check if we should show subscription alert
  const showSubscriptionAlert = useMemo(() => {
    return ready && subscription && (isDashboardPath || isClientPath);
  }, [ready, subscription, isDashboardPath, isClientPath]);

  const renderLayout = useCallback(() => {
    // 1. Dashboard Path - uses DashboardLayout with custom theme
    if (isDashboardPath) {
      return (
        <DashboardLayout>
          {showSubscriptionAlert && <SubscriptionAlert />}
          <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
            <Box
              sx={{
                opacity: !ready || showSubscriptionOverlay ? 0.4 : 1,
                pointerEvents:
                  !ready || showSubscriptionOverlay ? "none" : "auto",
                transition: "opacity 0.2s ease",
              }}
            >
              {children}
            </Box>
            {!ready && <LoadingOverlay />}
            {showSubscriptionOverlay && <SubscriptionOverlay xs={xs} />}
          </Box>
        </DashboardLayout>
      );
    }

    // 2. Client Path - uses base theme with client layout
    if (isClientPath) {
      return (
        <ThemeProvider theme={baseTheme}>
          <Box sx={{ display: "flex", minHeight: "100vh" }}>
            <SideNavBar />
            <Box sx={{ width: "100%", flexGrow: 1 }}>
              <Navbar />
              <Box
                sx={{
                  p: xs ? "10px" : "20px",
                  height: "calc(100vh - 60px)",
                  overflowY: "auto",
                  position: "relative",
                }}
              >
                {showSubscriptionAlert && <SubscriptionAlert />}
                {children}
              </Box>
            </Box>
          </Box>
        </ThemeProvider>
      );
    }

    // 3. Landing Path - uses base theme with landing layout
    if (isLandingPath) {
      return (
        <ThemeProvider theme={baseTheme}>
          <Box
            id="box-pettern"
            sx={{
              overflowX: "hidden",
              minHeight: "100vh",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            <StickyHeader isVisible={isHeaderVisible} xs={xs} />

            <Box
              sx={{
                mt: { xs: "4.8rem", md: "4.9rem" },
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                gap: { xs: 6, md: 8 },
              }}
            >
              {children}
            </Box>

            <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
              <Footer />
            </Box>
          </Box>
        </ThemeProvider>
      );
    }

    // 4. Fallback - uses base theme
    return (
      <ThemeProvider theme={baseTheme}>
        <Box id="box-pettern" sx={{ minHeight: "100dh" }}>
          {children}
        </Box>
      </ThemeProvider>
    );
  }, [
    isDashboardPath,
    isClientPath,
    isLandingPath,
    isHeaderVisible,
    xs,
    children,
    ready,
    showSubscriptionOverlay,
    showSubscriptionAlert,
  ]);

  // Wrap everything with DialogProvider
  const layoutContent = useMemo(() => {
    return <DialogProvider>{renderLayout()}</DialogProvider>;
  }, [renderLayout]);

  return <Suspense fallback={<SuspanceLoading />}>{layoutContent}</Suspense>;
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default memo(Layout);
